import { Component, TemplateRef, ViewEncapsulation, computed, effect, inject, signal, viewChild } from '@angular/core';
import { AppState } from 'src/app/app.state';
import { MatDialog } from '@angular/material/dialog';
import { PriceSignAllocationModalComponent } from '../price-sign-allocation-modal/price-sign-allocation-modal.component';
import { IncidentsQueryParams, PriceSignListStateService } from './price-sign.state.service';
import { Observable } from 'rxjs';
import { TextSearchComponent } from 'src/app/shared/components/text-search/text-search.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RouterLink } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import {
  CardComponent,
  ExpandListComponent,
  ExpandListConfiguration,
  ExpandListSort,
  FeedbackFailComponent,
} from 'ok-angular-shared/components';
import { DkCurrencyPipe } from 'src/app/shared/pipes/dk-currency';
import { AllocatedPriceSign } from '../price-sign.model';

// Maps expand-list columns to the sort keys expected by the backend search endpoint.
const SORT_FIELD_BY_COLUMN: Partial<Record<keyof AllocatedPriceSign, string>> = {
  vendor_identifier: 'vendorIdentifier',
  charging_station_location_name: 'chargingStationLocationName',
  organization_name: 'organizationName',
};

@Component({
  selector: 'adm-price-sign-overview',
  templateUrl: './price-sign-overview.component.html',
  styleUrl: './price-sign-overview.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    TextSearchComponent,
    MatButton,
    AsyncPipe,
    FeedbackFailComponent,
    MatPaginatorModule,
    MatTooltipModule,
    MatIconModule,
    RouterLink,
    DkCurrencyPipe,
    CardComponent,
    ExpandListComponent,
  ],
  providers: [PriceSignListStateService],
})
export class PriceSignOverviewComponent {
  appState = inject(AppState);
  private matDialog = inject(MatDialog);
  state = inject(PriceSignListStateService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);

  public currentSort = signal<ExpandListSort<AllocatedPriceSign>>({ column: 'vendor_identifier', direction: 'asc' });

  private vendorNoteTemplate = viewChild<TemplateRef<unknown>>('vendorNoteTemplate');
  private locationTemplate = viewChild<TemplateRef<unknown>>('locationTemplate');
  private organizationTemplate = viewChild<TemplateRef<unknown>>('organizationTemplate');
  private pricesHeaderTemplate = viewChild<TemplateRef<unknown>>('pricesHeaderTemplate');
  private pricesTemplate = viewChild<TemplateRef<unknown>>('pricesTemplate');

  public config = computed<ExpandListConfiguration<AllocatedPriceSign>>(() => ({
    expandDisabled: true,
    textLineClamp: 2,
    columns: [
      { field: 'vendor_note', header: 'Note', disableSort: true, displayTemplate: this.vendorNoteTemplate()! },
      { field: 'vendor_identifier', header: 'Skilt ID', sortField: 'vendorIdentifier' },
      {
        field: 'charging_station_location_name',
        header: 'Lokation',
        width: '2fr',
        sortField: 'chargingStationLocationName',
        displayTemplate: this.locationTemplate()!,
      },
      {
        field: 'organization_name',
        header: 'Organisation',
        width: '2fr',
        sortField: 'organizationName',
        displayTemplate: this.organizationTemplate()!,
      },
      {
        field: 'price_sign_prices',
        header: this.pricesHeaderTemplate()!,
        disableSort: true,
        displayTemplate: this.pricesTemplate()!,
      },
    ],
    actions: [
      {
        icon: { name: 'location-s' },
        label: 'Lokationstilknytning',
        action: item => item && this.openAllocatePriceSign(item),
      },
    ],
  }));

  constructor() {
    effect(() => {
      const filters = this.state.filters();
      const queryParams: Record<IncidentsQueryParams, string | null> = {
        [IncidentsQueryParams.query]: filters.query ? encodeURIComponent(filters.query) : null,
      };

      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: 'merge',
      });
    });
  }

  openUnallocatedPriceSigns() {
    const dialogRef = this.matDialog.open(PriceSignAllocationModalComponent, {
      data: { unallocatedPriceSigns$: this.state.$unallocatedPriceSigns },
      width: '1024px',
    });

    dialogRef.afterClosed().subscribe(() => {
      this.state.triggerUpdate();
    });
  }

  public resetFilter() {
    this.state.resetFilter();
  }

  querySearchInput = (source: Observable<string>) => {
    source.subscribe(value => {
      this.state.filters.update(x => ({
        ...x,
        query: value && value.length > 0 ? value : null,
        page: { ...x.page, pageIndex: 0 },
      }));
    });
  };

  public pageSwitch(event: PageEvent) {
    this.state.filters.update(x => ({ ...x, page: event }));
  }

  public onSortChange(sort: ExpandListSort<AllocatedPriceSign>) {
    this.currentSort.set(sort);

    const active = SORT_FIELD_BY_COLUMN[sort.column as keyof AllocatedPriceSign] ?? (sort.column as string);
    this.state.filters.update(x => ({
      ...x,
      sort: { active, direction: sort.direction },
      page: { ...x.page, pageIndex: 0 },
    }));
  }

  openAllocatePriceSign(priceSign: AllocatedPriceSign) {
    const dialogRef = this.matDialog.open(PriceSignAllocationModalComponent, {
      data: { priceSign },
      width: '1024px',
    });

    dialogRef.afterClosed().subscribe(() => {
      this.state.triggerUpdate();
    });
  }
}
